import { UnexpectedException } from '@maia/exceptions';
import { Logger } from '@maia/logger';
import { AddressDto } from '@maia/shared';
import { useMutation } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { debounce } from 'es-toolkit';
import { useMemo } from 'react';

interface PostalLookupResponse {
  response?: {
    postcode: string;
    number: number;
    street: string;
    city: string;
    municipality: string;
    province: string;
    location: {
      type: 'Point';
      coordinates: [number, number];
    };
  } | null;
  error: string | null;
  success: boolean;
}

export const usePostalCodeLookup = ({
  onSuccess,
  onError,
}: {
  onSuccess: (args: PostalLookupResponse) => void;
  onError: (error: unknown) => void;
}) => {
  const {
    mutateAsync: fetchAddress,
    isIdle,
    isSuccess,
    isError,
    isPending,
    isPaused,
  } = useMutation({
    mutationFn: async (args: { postalCode: string; houseNumber: string }) => {
      const url = `${import.meta.env.VITE_MAIA_API_PATH}/api/lookup/${args.postalCode}/${args.houseNumber}`;

      const response = await axios.get<PostalLookupResponse>(url, {
        adapter: 'fetch',
      });
      return response.data;
    },
    onSuccess,
    onError: (error: AxiosError) => {
      if (error?.status !== 404) {
        Logger.captureException(
          new UnexpectedException({
            entity: 'add-customer',
            service: 'postalcode-lookup-api',
            request: {
              headers: error.config?.headers.toJSON(true) as Record<
                string,
                string
              >,
              method: error.config?.method,
              url: error.config?.url,
            },
            response: {
              headers:
                typeof error.response?.headers?.toJSON === 'function'
                  ? (error.response?.headers?.toJSON(true) as Record<
                      string,
                      string
                    >)
                  : undefined,
              response: error.response?.data,
              statusCode: error.response?.status,
            },
          }),
          'error',
        );
      }
    },
  });

  const handleInputChange = useMemo(() => {
    return debounce(
      async (address?: Partial<AddressDto>) => {
        if (address?.postalCode && address?.number) {
          try {
            await fetchAddress({
              postalCode: address.postalCode.replace(/\s+/g, ''),
              houseNumber: address.number.toString(),
            });
          } catch (error) {
            onError(error);
          }
        }
      },
      450, // debounce delay
    );
  }, [fetchAddress, onError]);

  return { handleInputChange, isIdle, isError, isSuccess, isPending, isPaused };
};
